import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { AppConfigService } from '../../core/services/config.service';
import { ApplicationInfoService } from '../../core/translate/application-info.service';
import { TransactionResult } from '../../core/services/interfaces/transaction-result';
import { TranslateApplicationService } from '../../core/translate/translate-application-service';
import { Application } from '../../core/translate/models/application';
import { CognitoService } from '../../core/services/cognito/cognito.service';
import { NavigationService } from 'src/app/core/components/navigation/navigation.service';
import { AppNavigationModel } from 'src/app/navigation/navigation.model';
import { EmployeeService } from '../content/sections/services/employee-service';
import { IEmployeefromsap } from '../content/sections/Interfaces/IEmployeefromsap';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { IRoles } from '../content/sections/Interfaces/IRoles';
import { ISites } from '../content/sections/Interfaces/ISites';
import { SitesService } from '../../main/content/sections/services/sites-services';
import { ActiveDirectoryService } from '../../main/content/sections/services/activedirectory-service';
import { IEmployeesInActiveDirectory } from '../content/sections/Interfaces/IEmployeesInActiveDirectory';
import { EnvironmentsService } from '../content/sections/services/environments-services';
import { ToolbarStateService } from '../services/toolbar-state-service';
import { IWorkdayEmployee } from '../content/sections/Interfaces/IWorkdayEmployee';
import { LocationsService } from '../content/sections/services/locations-service';
import { ILocation } from '../content/sections/Interfaces/ILocation';
import { NotificationService } from 'src/app/core/services/notification/notification.service';

@Component({
	selector: 'app-toolbar',
	templateUrl: './toolbar.component.html',
	styleUrls: ['./toolbar.component.scss'],
})
export class AppToolbarComponent implements OnChanges {
	@Input() languages: Array<any> = [];
	@Input() listSites: Array<any> = [];
	isAdminUser = false;
	userStatusOptions: any[];
	selectedLanguage: any;
	showLoadingBar: boolean;
	horizontalNav: boolean;
	username: string;
	isAuthenticated: boolean;
	currentSite: Array<ISites> = [];
	formGroup: FormGroup;
	isTestingEnvironment: boolean;

	constructor(
		private router: Router,
		private appConfig: AppConfigService,
		private translate: TranslateApplicationService,
		private employeeService: EmployeeService,
		private locationsService: LocationsService,
		private activeDirectoryService: ActiveDirectoryService,
		private formBuilder: FormBuilder,
		private appNavigationService: NavigationService,
		private cognitoService: CognitoService,
		private toolbarStateService: ToolbarStateService,
		private notificationService: NotificationService
	) {
		// Initializes site
		const fkSite = this.listSites.length > 0 ? this.listSites[0].pk_site : null;
		this.formGroup = this.formBuilder.group({
			fkSite: [fkSite, Validators.required],
		});
		this.toolbarStateService.ChangeSite(fkSite);

		// Subscribe to site changes
		this.toolbarStateService.site$.subscribe( (site) => {
			this.appNavigationService.setNavigationModel(
				new AppNavigationModel(),
			);
		});

		// Retrieves user data
		this.UserInfoRetrieve();

		router.events.subscribe((event) => {
			if (event instanceof NavigationStart) {
				this.showLoadingBar = true;
			}
			if (event instanceof NavigationEnd) {
				this.showLoadingBar = false;
			}
		});

		this.appConfig.onSettingsChanged.subscribe((settings) => {
			this.horizontalNav = settings.layout.navigation === 'top';
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if(changes['listSites']){
			if(!this.listSites){
				this.listSites = [];
			}
			const fkSite = this.listSites.length > 0 ? this.listSites[0].pk_site : null;
			if(fkSite){
				this.formGroup.controls.fkSite.setValue(fkSite);
				this.toolbarStateService.ChangeSite(this.listSites[0].pk_site);
				this.GetLanguage(this.listSites[0]);
			}
			
		}
	}

	UserInfoRetrieve(){
		this.cognitoService.getUserInfo().subscribe(
			(result: any) => {
				// By default STG or PRD
				let userNT =  result['custom:samaccountname'];
				let email = result['email'];
				this.username = email;
				localStorage.setItem('username', userNT);
				localStorage.setItem('email', email);
				// Retrieves from work day
				this.WorkdayUserInfoRetrieve(userNT);
			}
		);
	}

	WorkdayUserInfoRetrieve(username: string) {
		this.activeDirectoryService.GetEmployeeByEmailNumberName(username).subscribe(
			(result: TransactionResult<IEmployeesInActiveDirectory[]>) => {
				if(!result.success){
					this.notificationService.AddAlert({
						icon: 'all_inbox',
						title: 'Error',
						message: result.message,
						shown: true,
					});
					return;
				}

				const employee = result.data.find( x => x.userName == username );
				if(!employee){
					const employee_not_found_text = this.translate.TranslateKey("ERRORS.employee_not_found");
					this.notificationService.AddAlert({
						icon: 'all_inbox',
						title: 'Error',
						message: `${username}: ${employee_not_found_text}`,
						shown: true,
					});
					return;
				}
				this.activeDirectoryService.employee = employee;
				this.UserRoleRetrieve();

				this.employeeService.GetUserInfo(employee.workdayId).subscribe(
					( result: TransactionResult< IWorkdayEmployee > ) => {
						if(!result.success){
							this.notificationService.AddAlert({
								icon: 'all_inbox',
								title: 'Error',
								message: result.message,
								shown: true,
							});
							return;
						}
				
						// Sets employee data
						const employeeData = result.data;

						// Try to retrieve a site through location
						this.locationsService.GetByName(employeeData.location).subscribe(
							(result: TransactionResult<ILocation[]>) => {
								if(!result.success){
									this.notificationService.AddAlert({
										icon: 'all_inbox',
										title: 'Error',
										message: result.message,
										shown: true,
									});
									return;
								}

								if(result.data.length <= 0) {
									const location_not_found_text = this.translate.TranslateKey("ERRORS.location_not_found");
									this.notificationService.AddAlert({
										icon: 'all_inbox',
										title: 'Error',
										message: `${employeeData.location}: ${location_not_found_text}`,
										shown: true,
									});
									return;
								}

								// Gets the assigned site
								const siteData = {
									pk_site: result.data[0].site.pk_site,
									site: result.data[0].site.site,
									language: result.data[0].site.language.language,
									available: result.data[0].site.available
								};
								
								// Adds to the list
								this.toolbarStateService.ChangeSite(siteData.pk_site);
								this.listSites = this.listSites.filter(item => item.pk_site != siteData.pk_site);
								this.listSites.push(siteData);
								this.GetLanguage(siteData);
								this.formGroup.controls.fkSite.setValue(siteData.pk_site);
							},
							(error: any) => {}
						)
					},
				);
			},
			(error: any) => {}
		);
	}

	UserRoleRetrieve(){
		this.employeeService.IsAdmin().subscribe(
			(resultAdmin: TransactionResult<IRoles>) => {
				if(!resultAdmin.success){
					this.router.navigateByUrl('/main/without-permission');
					return;
				}
				
				this.isAdminUser = false;
				let roles = ['Local Administrator', 'Regional Administrator'];
				if( roles.includes(resultAdmin.data[0].role)){
					this.isAdminUser = true;
				}
				
				localStorage.setItem('role', resultAdmin.data[0].role);
			}
		);
	}

	ChangeSite() {
		if (this.cognitoService.IsAuhtenticated()) {
			const fkSite = this.formGroup.value.fkSite;
			this.toolbarStateService.ChangeSite(fkSite);
		}
	}

	GetLanguage(site: any) {
		if(site){
			const first = site;
			let changeToLanguage = this.languages.filter(
				(element) => element.title == first.language,
			);
			this.SetLanguage(changeToLanguage[0]);
		}
	}

	SelectLanguage(): void {
		let indexLang = 0;
		if (this.languages != undefined) {
			if (navigator.language.split('-').length > 0) {
				indexLang = this.languages.findIndex(
					(lang) => lang.flag === navigator.language.split('-')[0],
				);
			} else {
				indexLang = this.languages.findIndex(
					(lang) => lang.flag === navigator.language,
				);
			}

			if (indexLang < 0) {
				this.selectedLanguage = this.languages[0];
			} else {
				this.selectedLanguage = this.languages[indexLang];
			}

			this.SetLanguage(this.selectedLanguage);
		}
	}

	SetLanguage(lang) {
		// Set the selected language for toolbar
		this.selectedLanguage = lang;
		// selected language for translations
		this.translate.use(lang);
	}

	logOut(): void {
		localStorage.removeItem('email');
		localStorage.removeItem('fksite');
		localStorage.removeItem('role');
		this.cognitoService.logOut();
	}
}
